import { Accordion, cn } from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { ProductDetailsNoTerpenesInfo } from "~/features/products/product-details-no-terpenes-info.component";
import { ProductDetailsTerpenesContent } from "./product-details-terpenes-content.component";
import { ProductDetailsTitle } from "./product-details-title.component";

const terpenesAccordionVariants = cva("flex flex-col", {
	variants: {
		variant: {
			noData: "",
			default: "gap-3.5",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

type ProductDetailsTerpenesProps = {
	terpenes: ProductNoEdibleInfo["terpenes"];
	variant?: VariantProps<typeof terpenesAccordionVariants>["variant"];
};

export function ProductDetailsTerpenes({
	terpenes,
	variant = "default",
}: ProductDetailsTerpenesProps) {
	const { t } = useTranslation(["common", "product"]);
	const hasData = terpenes.length > 0;

	return (
		<div className={cn(terpenesAccordionVariants({ variant }))}>
			<Accordion.Trigger>
				<ProductDetailsTitle
					title={t("common:terpenes")}
					info={t("common:terpenesInfo")}
				/>
			</Accordion.Trigger>

			<Accordion.Content className={cn(terpenesAccordionVariants({ variant }))}>
				{!hasData && <ProductDetailsNoTerpenesInfo />}

				{terpenes.map(({ name, color, effects, flavors, complaints }) => {
					return (
						<div
							key={`terpenes-${name}`}
							className="relative flex flex-col gap-2 pl-4 text-gray-700"
						>
							<div
								className="absolute left-0 top-0 w-1 h-full rounded-full"
								style={{ backgroundColor: color }}
							/>

							<p className="text-h4 first-letter:uppercase">{name}</p>

							{effects.length > 0 && (
								<ProductDetailsTerpenesContent
									characteristicName={t("common:effects")}
									characteristicValue={effects.map((effect) =>
										// @ts-ignore: dynamic key generation
										t(`product:filters.effects.${effect}`),
									)}
								/>
							)}

							{flavors.length > 0 && (
								<ProductDetailsTerpenesContent
									characteristicName={t("common:flavors")}
									characteristicValue={flavors.map((flavor) =>
										// @ts-ignore: dynamic key generation
										t(`product:filters.flavors.${flavor}`),
									)}
								/>
							)}

							{complaints.length > 0 && (
								<ProductDetailsTerpenesContent
									characteristicName={t("common:complaints")}
									characteristicValue={complaints.map((complaint) =>
										// @ts-ignore: dynamic key generation
										t(`product:filters.complaints.${complaint}`),
									)}
								/>
							)}
						</div>
					);
				})}
			</Accordion.Content>
		</div>
	);
}
